import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel
} from "@mui/material";
import { ProductRow } from "./ProductRow";
import { useState, useMemo, useRef, useEffect } from "react";
import { Product } from "../../../../../models/DB";
import { ProductsService } from "../../../../../services/supabase/ProductsService";
import { TableControlBar } from "../../../../../components/TableControlBar";
import { PortalTable } from "../../../../../components/PortalTable";
import { productStore } from "../../../../../app/stores/Product";
import { ProductFilters, ProductFilterConfig } from "../../../../../components/ProductFilters";
import { STOCK_LEVELS } from "../../../../../config/constants";
import { tablePreferencesStore } from "../../../../../app/stores/TablePreferences";

interface ProductsTableProps {
    products: Product[];
    onEditProduct?: (product: Product) => void;
}

export const ProductsTable = ({ products, onEditProduct }: ProductsTableProps) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);

    // Get preferences from store
    const {
        products: productsPrefs,
        updateProductsPreferences,
        updateProductsFilters
    } = tablePreferencesStore();

    // Use store values with local state for page and search (not persisted)
    const [order, setOrder] = useState<'asc' | 'desc'>(productsPrefs.sortOrder);
    const [orderBy, setOrderBy] = useState<keyof Product>(productsPrefs.sortBy as keyof Product);
    const [page, setPage] = useState(0); // Page is session-only, not persisted
    const [rowsPerPage, setRowsPerPage] = useState<number>(productsPrefs.rowsPerPage);
    const [dense, setDense] = useState(productsPrefs.dense);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState(''); // Search is session-only
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filters, setFilters] = useState<ProductFilterConfig>(productsPrefs.filters);
    const { getAllProducts } = ProductsService();

    // Debounce search with 300ms delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Get unique categories for filter
    const uniqueCategories = useMemo(() => {
        const cats = new Set<string>();
        products?.forEach(p => cats.add(p.category || 'Uncategorized'));
        return Array.from(cats).sort();
    }, [products]);


    // Filter products with comprehensive filters and debounced search
    const filteredProducts = useMemo(() => {
        let filtered = products || [];

        // Stock status filter
        if (filters.stockStatus === 'in-stock') {
            filtered = filtered.filter(p => (p.stockOnHand || 0) > filters.lowStockThreshold);
        } else if (filters.stockStatus === 'low-stock') {
            filtered = filtered.filter(p => (p.stockOnHand || 0) > 0 && (p.stockOnHand || 0) <= filters.lowStockThreshold);
        } else if (filters.stockStatus === 'out-of-stock') {
            filtered = filtered.filter(p => (p.stockOnHand || 0) === 0);
        }

        // Price range filter
        if (filters.priceMin !== null) {
            filtered = filtered.filter(p => (p.price || 0) >= filters.priceMin!);
        }
        if (filters.priceMax !== null) {
            filtered = filtered.filter(p => (p.price || 0) <= filters.priceMax!);
        }

        // Discount range filter
        if (filters.discountMin !== null) {
            filtered = filtered.filter(p => (p.discount || 0) >= filters.discountMin!);
        }
        if (filters.discountMax !== null) {
            filtered = filtered.filter(p => (p.discount || 0) <= filters.discountMax!);
        }

        // Category filter
        if (filters.categories.length > 0) {
            filtered = filtered.filter(p => filters.categories.includes(p.category || ''));
        }

        // Variant status filter
        if (filters.variantStatus === 'with-variants') {
            filtered = filtered.filter(p => (p.variantOptions?.length || 0) > 0);
        } else if (filters.variantStatus === 'no-variants') {
            filtered = filtered.filter(p => (p.variantOptions?.length || 0) === 0);
        }

        // Active status filter
        if (filters.activeStatus === 'active') {
            filtered = filtered.filter(p => p.isActive !== false);
        } else if (filters.activeStatus === 'inactive') {
            filtered = filtered.filter(p => p.isActive === false);
        }

        // Debounced search filter (searches across all fields)
        if (debouncedSearch.trim()) {
            const query = debouncedSearch.toLowerCase();
            filtered = filtered.filter(p => (
                p.name?.toLowerCase().includes(query) ||
                p.category?.toLowerCase().includes(query) ||
                p.code?.toLowerCase().includes(query) ||
                p.variantType?.toLowerCase().includes(query)
            ));
        }

        return filtered;
    }, [products, filters, debouncedSearch]);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredProducts.length) : 0;

    const sortedProducts = filteredProducts.slice().sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Special handling for variantOptions (array)
        if (orderBy === 'variantOptions') {
            aValue = (a.variantOptions?.length || 0) as any;
            bValue = (b.variantOptions?.length || 0) as any;
        }

        // Special handling for isActive (boolean)
        if (orderBy === 'isActive') {
            aValue = (a.isActive !== false ? 1 : 0) as any;
            bValue = (b.isActive !== false ? 1 : 0) as any;
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) {
            if (bValue === null || bValue === undefined) return 0;
            return 1; // Push nulls to the end
        }
        if (bValue === null || bValue === undefined) {
            return -1; // Push nulls to the end
        }

        // String columns
        if (orderBy === 'name' || orderBy === 'category' || orderBy === 'updated' || orderBy === 'created') {
            const comparison = String(aValue).localeCompare(String(bValue));
            return order === 'asc' ? comparison : -comparison;
        }

        // Number and boolean columns
        if (orderBy === 'price' || orderBy === 'costPrice' || orderBy === 'discount' ||
            orderBy === 'stockOnHand' || orderBy === 'variantOptions' || orderBy === 'isActive') {
            const comparison = (aValue as number) - (bValue as number);
            return order === 'asc' ? comparison : -comparison;
        }

        // Default: treat as number
        const comparison = (aValue as number) - (bValue as number);
        return order === 'asc' ? comparison : -comparison;
    });

    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof Product) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);

        // Persist to store
        updateProductsPreferences({
            sortOrder: newOrder,
            sortBy: property as string
        });
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
        // Scroll to top of table when changing pages
        tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentScrollY = window.scrollY;
        const newRowsPerPage = +event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);

        // Persist to store
        updateProductsPreferences({
            rowsPerPage: newRowsPerPage
        });

        // Restore scroll position after React re-renders
        requestAnimationFrame(() => {
            window.scrollTo({ top: currentScrollY, behavior: 'instant' });
        });
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        await getAllProducts();
        setIsLoading(false);
    };


    return (
        <Box
            ref={tableContainerRef}
            sx={{
                width: '100%',
                mb: 4,
            }}
        >
            {/* Controls Bar */}
            <TableControlBar
                dense={dense}
                onDenseChange={(newDense) => {
                    setDense(newDense);
                    updateProductsPreferences({ dense: newDense });
                }}
                isLoading={isLoading}
                onRefresh={handleRefresh}
                search={{
                    value: searchQuery,
                    onChange: setSearchQuery,
                    placeholder: 'Search products...'
                }}
                activeFilterCount={(() => {
                    let count = 0;
                    if (filters.stockStatus !== 'all') count++;
                    if (filters.activeStatus !== 'all') count++;
                    if (filters.categories.length > 0) count++;
                    if (filters.variantStatus !== 'all') count++;
                    if (filters.priceMin !== null || filters.priceMax !== null) count++;
                    if (filters.discountMin !== null || filters.discountMax !== null) count++;
                    if (debouncedSearch) count++;
                    return count;
                })()}
                filterDescription={(() => {
                    const activeFilters: string[] = [];

                    if (filters.stockStatus !== 'all') {
                        activeFilters.push(
                            filters.stockStatus === 'in-stock' ? 'In Stock' :
                                filters.stockStatus === 'low-stock' ? 'Low Stock' : 'Out of Stock'
                        );
                    }

                    if (filters.activeStatus !== 'all') {
                        activeFilters.push(filters.activeStatus === 'active' ? 'Active' : 'Inactive');
                    }

                    if (filters.categories.length > 0) {
                        activeFilters.push(`${filters.categories.length} ${filters.categories.length === 1 ? 'category' : 'categories'}`);
                    }

                    if (filters.variantStatus !== 'all') {
                        activeFilters.push(filters.variantStatus === 'with-variants' ? 'With Variants' : 'No Variants');
                    }

                    if (filters.priceMin !== null || filters.priceMax !== null) {
                        activeFilters.push('Price filtered');
                    }

                    if (filters.discountMin !== null || filters.discountMax !== null) {
                        activeFilters.push('Discount filtered');
                    }

                    const baseText = `Showing ${filteredProducts.length} of ${products?.length || 0} products`;

                    if (activeFilters.length > 0) {
                        return `${baseText} • ${activeFilters.join(', ')}${debouncedSearch ? ` • Search: "${debouncedSearch}"` : ''}`;
                    }

                    return debouncedSearch ? `${baseText} • Search: "${debouncedSearch}"` : baseText;
                })()}
                customActions={
                    <ProductFilters
                        filters={filters}
                        onChange={(newFilters) => {
                            setFilters(newFilters);
                            setPage(0);
                            // Persist filters to store
                            updateProductsFilters(newFilters);
                        }}
                        availableCategories={uniqueCategories}
                    />
                }
            />

            <PortalTable>
                <TableContainer>
                    <Table stickyHeader size={dense ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        sx={{ ml: 1, fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'name')}
                                    >
                                        Name
                                    </TableSortLabel>
                                </TableCell>
                                {/* Category - hidden on mobile */}
                                <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'category'}
                                        direction={orderBy === 'category' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'category')}
                                    >
                                        Category
                                    </TableSortLabel>
                                </TableCell>
                                {/* Mobile: Combined Stock/Status header */}
                                <TableCell align='right' sx={{ display: { xs: 'table-cell', sm: 'none' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1, fontSize: '0.75rem' }}
                                        active={orderBy === 'stockOnHand'}
                                        direction={orderBy === 'stockOnHand' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'stockOnHand')}
                                    >
                                        Stock
                                    </TableSortLabel>
                                </TableCell>
                                {/* Desktop: Status header */}
                                <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'isActive'}
                                        direction={orderBy === 'isActive' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'isActive')}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'price'}
                                        direction={orderBy === 'price' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'price')}
                                    >
                                        Price
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'costPrice'}
                                        direction={orderBy === 'costPrice' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'costPrice')}
                                    >
                                        Cost Price
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'discount'}
                                        direction={orderBy === 'discount' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'discount')}
                                    >
                                        Discount
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'stockOnHand'}
                                        direction={orderBy === 'stockOnHand' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'stockOnHand')}
                                    >
                                        Stock Available
                                    </TableSortLabel>
                                </TableCell> 
                                <TableCell align='right' sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'variantOptions'}
                                        direction={orderBy === 'variantOptions' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'variantOptions')}
                                    >
                                        Variants
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align='right' sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', letterSpacing: 1 }}
                                        active={orderBy === 'updated'}
                                        direction={orderBy === 'updated' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'updated')}
                                    >
                                        Last Updated
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product) => {
                                return (
                                    <ProductRow
                                        key={product.id}
                                        product={product}
                                        onEdit={onEditProduct}
                                    />
                                );
                            })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                    <TableCell colSpan={9} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[8, 10, 25, 50, 100]}
                    component='div'
                    count={filteredProducts.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-select': {
                            fontFamily: 'primaryFont'
                        },
                        '& .MuiTablePagination-displayedRows': {
                            fontFamily: 'primaryFont'
                        }
                    }}
                />
            </PortalTable>
        </Box>
    );
};